import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonsContainer,
  Heading,
  Text,
  WithTooltip
} from 'components/common/Elements/Elements';
import { Select } from '@p81-common/ui';
import { modalActionBody } from 'components/common/Elements/NewModal/newModal';

import { renameMacOS, underlineToSpace } from 'utils/common';
import { access, hasAccess, resources } from 'utils/permissions';
import { RELEASE_TRACKS } from '../../../RolloutVersions/helpers/configurates';

import classes from './AgentInfo.module.scss';

interface AgentFile {
  buildId: string;
  osDist: string;
}

interface AgentData {
  version: string;
  platform: string;
  arch: string;
  status: string[];
  files: AgentFile[];
  releaseTracks?: string[];
  isDefaultForNewTenants?: boolean;
}

interface TrackHolder {
  version: string;
  releaseTracks?: string[];
}

interface ActionResult {
  statusCode?: number;
}

interface AgentInfoProps {
  data: AgentData;
  isVisibleButtons?: boolean;
  handleCancel: () => void;
  handleRollout: () => void;
  isNotFiltered: boolean;
  buildIds: string[];
  handleSetAsDefault: () => Promise<ActionResult>;
  trackHolders?: TrackHolder[];
  handleSetVersionTrack: (track: string) => Promise<ActionResult | undefined>;
}

const AgentInfo = ({
  data,
  isVisibleButtons,
  handleCancel,
  handleRollout,
  isNotFiltered,
  buildIds,
  handleSetAsDefault,
  trackHolders = [],
  handleSetVersionTrack
}: AgentInfoProps) => {
  const [isDefault, setAsDefault] = useState(data.isDefaultForNewTenants);
  const [assignedTracks, setAssignedTracks] = useState<Record<string, string>>(
    {}
  );
  const { t } = useTranslation(['activity']);
  const hasAgentRolloutWriteAccess = hasAccess([
    resources.CS_AGENT_ROLLOUT,
    access.WRITE
  ]);
  const canSetVersionTrack = hasAccess([
    resources.CS_AGENT_ROLLOUT_TRACK,
    access.WRITE
  ]);
  const isLinux = data.platform === 'linux';

  const handleSetAsDefaultClick = async () => {
    modalActionBody({
      title: t('activity:agentRollout.SET_AS_DEFAULT_FOR_NEW_TENANTS_TITLE'),
      callBack: async () => {
        const result = await handleSetAsDefault();
        result.statusCode === 202 && setAsDefault(true);
      },
      text: t(
        'activity:agentRollout.SET_AS_DEFAULT_FOR_NEW_TENANTS_MODAL_TEXT',
        {
          version: data.version,
          platform: data.platform
        }
      ),
      okText: t('activity:agentRollout.SET_AS_DEFAULT_CONFIRM'),
      cancelText: t('activity:featureFlag.NO_THANKS')
    });
  };

  const trackLabel = (track: string) =>
    t(`activity:agentRollout.RELEASE_TRACK_${track}`);

  const handleTrackClick = (track: string) => {
    modalActionBody({
      title: t('activity:agentRollout.SET_VERSION_TRACK_TITLE'),
      callBack: async () => {
        const result = await handleSetVersionTrack(track);
        const statusCode = result?.statusCode ?? 0;

        if (statusCode >= 200 && statusCode < 300) {
          setAssignedTracks(prev => ({ ...prev, [track]: data.version }));
        }
      },
      text: t('activity:agentRollout.SET_VERSION_TRACK_MODAL_TEXT', {
        version: data.version,
        track: trackLabel(track),
        platform: renameMacOS(data.platform)
      }),
      okText: t('activity:agentRollout.SET_VERSION_TRACK_CONFIRM'),
      cancelText: t('activity:featureFlag.NO_THANKS')
    });
  };

  const trackHolder = (track: string) =>
    assignedTracks[track] ||
    trackHolders.find(build => (build.releaseTracks || []).includes(track))
      ?.version;

  const currentTracks = RELEASE_TRACKS.filter(
    track =>
      track in assignedTracks || (data.releaseTracks || []).includes(track)
  );

  const trackListItems = RELEASE_TRACKS.map(track => {
    const holder = trackHolder(track);

    return {
      id: track,
      name: track,
      textNode: (
        <span>
          {trackLabel(track)}
          {holder ? ` — ${holder}` : ''}
          {currentTracks.includes(track)
            ? ` (${t('activity:agentRollout.CURRENTLY_ASSIGNED')})`
            : ''}
        </span>
      )
    };
  });

  // Shown to every role that can open the page, so a role without the track
  // permission sees why it is disabled instead of a missing control.
  const trackSelect = (
    <Select
      value={currentTracks[0] || ''}
      disabled={!canSetVersionTrack}
      menuContentProps={{
        listItems: trackListItems,
        handleClick: name => () => handleTrackClick(name as string)
      }}
      renderValue={() => t('activity:agentRollout.SET_VERSION_TRACK')}
      menuWidth={360}
      label=""
      error={false}
      placeholder={t('activity:agentRollout.SET_VERSION_TRACK')}
    />
  );

  const createAgent = (agent: AgentData) => {
    return [
      {
        title: `Version:`,
        value: agent.version
      },
      {
        title: `Platform:`,
        value: `${renameMacOS(agent.platform)}${
          agent.platform === 'linux'
            ? ` - ${
                agent.files.find(item => item.buildId === buildIds[0])!.osDist
              }`
            : ''
        }`,
        additional: agent.arch
      },
      {
        title: `Status:`,
        // status carries one entry per build (arch x osDist) - collapse repeats
        value: underlineToSpace(Array.from(new Set(agent.status)).join(', '))
      },
      ...(!isLinux
        ? [
            {
              title: t('activity:agentRollout.CURRENT_LABEL'),
              value: currentTracks.length
                ? currentTracks.map(trackLabel).join(', ')
                : t('activity:agentRollout.SLOT_NOT_ASSIGNED')
            }
          ]
        : [])
    ];
  };

  return (
    <div className={classes.cell_border}>
      <ul className={classes.cell_wrap}>
        {createAgent(data).map((item, index) => (
          <li className={classes.cell} key={index}>
            <Heading size={4} weight="r" className={classes.cell_title}>
              {item.title || <>&nbsp;</>}
            </Heading>
            <Text className="">
              <Text type="s" size={6} weight="m" className={classes.cell_value}>
                {item.value || <>&nbsp;</>}
              </Text>
              {item.additional && (
                <Text
                  type="s"
                  size={6}
                  weight="m"
                  className={classes.cell_additional}
                >
                  &nbsp;
                  {item.additional}
                </Text>
              )}
            </Text>
          </li>
        ))}
      </ul>
      {isVisibleButtons && (
        <div className={classes.actions}>
          {!isLinux &&
            (canSetVersionTrack ? (
              trackSelect
            ) : (
              // Select has no forwardRef, so the tooltip anchors on a wrapper.
              // WithTooltip's prop-types children type rejects JSX.Element.
              <WithTooltip
                text={t(
                  'activity:agentRollout.SET_VERSION_TRACK_NO_PERMISSION'
                )}
              >
                {(<div>{trackSelect}</div>) as any}
              </WithTooltip>
            ))}
          <ButtonsContainer
            handleCancel={handleCancel}
            btnAdditional={hasAgentRolloutWriteAccess && isLinux}
            btnOpen={hasAgentRolloutWriteAccess}
            btnCancel={hasAgentRolloutWriteAccess}
            handleOpenModal={handleRollout}
            handleOnAdditionalButtonClick={handleSetAsDefaultClick}
            additionalButtonDisabled={isDefault}
            additionalButtonText={
              isDefault
                ? t(
                    'activity:agentRollout.SET_AS_DEFAULT_FOR_NEW_TENANTS_ALREADY_SET'
                  )
                : t('activity:agentRollout.SET_AS_DEFAULT_FOR_NEW_TENANTS')
            }
            action={
              isNotFiltered
                ? t('activity:agentRollout.ROLLOUT_FOR_ALL')
                : t('activity:agentRollout.ROLLOUT_FOR_FILTERED')
            }
            classesBtn={
              isNotFiltered ? { action: classes.btnAction } : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

export default AgentInfo;
